import { Head, Link, WhenVisible, router, useForm, usePage } from "@inertiajs/react";
import { Ghost, Palette, Timer } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ActivationGuide } from "@/components/activation-guide";
import { FinalCta } from "@/components/final-cta";
import { GalleryCarousel } from "@/components/gallery-carousel";
import { HeroBeforeAfterGallery } from "@/components/hero-before-after-gallery";
import { PricingTable } from "@/components/pricing-table";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { StickySubscribeForm } from "@/components/sticky-subscribe-form";
import ArrowBottom from "@/components/ui/arrow-bottom";
import ArrowRight from "@/components/ui/arrow-right";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import FaqSection from "@/components/ui/faq-section";
import FeatureSection from "@/components/ui/feature-section";
import { Input } from "@/components/ui/input";
import { useAppearance } from "@/hooks/use-appearance";
import { useFaviconUrl } from "@/hooks/use-favicon-url";
import { useLeadDomain } from "@/hooks/use-lead-domain";
import { stripProtocol } from "@/lib/utils";
// Helper to track events with Rybbit + Ahoy (client-side only)
const trackEvent = async (name: string, props?: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  window.rybbit?.event(name, props as Record<string, string> | undefined);
  try {
    const { default: ahoy } = await import("ahoy.js");
    ahoy.track(name, props);
  } catch {
    // no-op
  }
};

const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const numberFormatter = new Intl.NumberFormat("en-US");

const formatPlanName = (plan?: string | null) => {
  if (!plan) return "Starter";
  return plan.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

// Basic cleanup without adding protocol - for real-time input handling
const cleanInputUrl = (rawValue: string) => {
  let input = rawValue.trim();

  // Remove leading/trailing whitespace and invisible chars
  input = input.replace(/^[\s\u200B-\u200D\uFEFF]+|[\s\u200B-\u200D\uFEFF]+$/g, "");

  // Remove accidental spaces in the middle
  input = input.replace(/\s+/g, "");

  return input;
};

// Full sanitization with protocol addition - for form submission only
const sanitizeUrl = (rawValue: string) => {
  const input = cleanInputUrl(rawValue);

  // If URL doesn't have protocol, add https://
  if (input && !/^https?:\/\//i.test(input) && /^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(input)) {
    return `https://${input}`;
  }

  return input;
};

export default function Welcome() {
  const { flags, auth, plan_quota } = usePage().props;
  const { data, setData, processing, errors } = useForm<{
    url: string;
    style: string;
  }>({
    url: "",
    style: "default",
  });
  const heroDomainForm = useForm<{ domain: string }>({ domain: "" });

  const { setLeadDomain } = useLeadDomain();
  const { appearance } = useAppearance();
  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (window.matchMedia("(min-width: 768px)").matches) {
      urlInputRef.current?.focus();
    }
  }, []);
  const pricingDomainInputRef = useRef<HTMLInputElement>(null);
  const [isValidDomain, setIsValidDomain] = useState(false);
  const faviconUrl = useFaviconUrl(heroDomainForm.data.domain);
  const isLoggedIn = Boolean(auth?.user);
  const currentPlanName = formatPlanName(plan_quota?.plan);
  const rawImageLimit = plan_quota?.image_limit ?? 0;
  const hasUnlimitedImageQuota = plan_quota?.unlimited ?? rawImageLimit < 0;
  const imageLimit = Math.max(rawImageLimit, 0);
  const hasImageQuota = hasUnlimitedImageQuota || imageLimit > 0;

  const updateDomain = (rawValue: string) => {
    const cleaned = stripProtocol(cleanInputUrl(rawValue)).split("/")[0];
    heroDomainForm.setData("domain", cleaned);
    heroDomainForm.clearErrors("domain");
    setData("url", cleaned);

    if (DOMAIN_PATTERN.test(cleaned)) {
      setIsValidDomain(true);
    } else {
      setIsValidDomain(false);
      setLeadDomain(null);
    }
  };

  const updateInputUrl = (rawValue: string) => {
    const cleanedUrl = cleanInputUrl(rawValue);
    setData("url", cleanedUrl);
  };

  const handleInputUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateInputUrl(event.target.value);
  };

  const handleInputUrlPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) return;

    updateInputUrl(stripProtocol(pastedText));
  };

  const handleDomainPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const pastedText = event.clipboardData.getData("text");
    if (!pastedText) return;

    updateDomain(pastedText);
  };

  const handleHeroDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDomain(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const cleanedUrl = sanitizeUrl(data.url);
    if (!cleanedUrl) return;

    if (flags.free_one_off_generation) {
      void trackEvent("click-generate-free", {
        url: cleanedUrl,
        style: data.style,
      });

      const payload: { url: string; style: string } = {
        url: cleanedUrl,
        style: data.style,
      };

      router.post(`/make-for-free`, payload, {
        onError: (error) => {
          console.error(error);
        },
      });
    } else {
      void trackEvent("click-checkout", { url: cleanedUrl });

      // Use full page redirect instead of AJAX to allow external redirects
      router.get(`/checkout?url=${encodeURIComponent(cleanedUrl)}`, {}, { replace: false });
    }
  };

  const submitLeadDomain = () => {
    const domain = heroDomainForm.data.domain.trim();
    if (!DOMAIN_PATTERN.test(domain)) {
      heroDomainForm.setError("domain", "Enter a valid domain before continuing.");
      urlInputRef.current?.focus();
      return;
    }

    heroDomainForm.post("/onboarding/add_domain", {
      preserveScroll: true,
      onSuccess: () => {
        setLeadDomain(domain);
        window.location.href = "/sign_up";
      },
      onError: () => {
        urlInputRef.current?.focus();
      },
    });
  };

  const handleHeroDomainSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (heroDomainForm.processing) return;
    submitLeadDomain();
  };

  const getCtaLabel = (freeLabel: string) => {
    if (flags.free_one_off_generation) return freeLabel;
    if (isLoggedIn) {
      if (!hasImageQuota) return "Upgrade plan to generate images";
      if (hasUnlimitedImageQuota) return `Use 1 quota image (unlimited on ${currentPlanName})`;
      return `Use 1 quota image (max ${numberFormatter.format(imageLimit)} on ${currentPlanName})`;
    }

    return "Get started for $2";
  };

  const renderPlanQuotaNotice = (variant: "light" | "dark") => {
    if (!isLoggedIn || hasImageQuota) return null;

    const isDark = variant === "dark";
    const accentText = isDark ? "text-orange-100" : "text-orange-700";
    const borderColor = isDark ? "border-orange-400/60 bg-orange-400/10" : "border-orange-200 bg-orange-50";

    return (
      <div className={`flex flex-col items-start justify-between gap-4 rounded-lg border ${borderColor} p-4`}>
        <div className="space-y-1">
          <p className={`text-sm font-semibold ${accentText}`}>
            {currentPlanName} includes no image quota. Upgrade to generate one-off images.
          </p>
        </div>
        <Button
          type="button"
          variant={isDark ? "secondary" : "outline"}
          size="sm"
          onClick={() => router.visit("/subscribe")}
        >
          Upgrade plan
        </Button>
      </div>
    );
  };

  const handlePricingDomainSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (heroDomainForm.processing) return;
    submitLeadDomain();
  };

  // Determine if dark mode is active
  const isDarkMode =
    appearance === "dark" ||
    (appearance === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const heroPreviewGallery = {
    before: {
      src: isDarkMode
        ? "/hero-before-vs-after/default-no-image-dark.webp"
        : "/hero-before-vs-after/default-no-image.webp",
      alt: "Generic social preview missing image with gray placeholder",
    },
    after: [
      {
        src: "/hero-before-vs-after/after-1.webp",
        alt: "OG Pilot GitHub dynamic contribution graph",
      },
      {
        src: "/hero-before-vs-after/after-2.webp",
        alt: "OG Pilot branded preview with dynamic job opening count",
      },
      {
        src: "/hero-before-vs-after/after-3.webp",
        alt: "OG Pilot landing page preview",
      },
      {
        src: "/hero-before-vs-after/after-4.webp",
        alt: "OG Pilot podcast page preview with episode details",
      },
      {
        src: "/hero-before-vs-after/after-5.webp",
        alt: "OG Pilot blog post page preview with article details",
      },
    ],
  };

  const heroHighlights = [
    {
      emoji: "🌐",
      copy: "Connect your domain",
    },
    { emoji: "🎨", copy: "We auto-pull your brand colors and logo" },
    { emoji: "🔧", copy: "SDKs for easy integrations" },
    { emoji: "🔄", copy: "Get a fresh new image every 24 hours" },
  ] as const;

  const industryProblems = [
    {
      title: "Broken or random previews",
      description: "Social apps grab the wrong image or none at all, so the link looks broken and people skip it.",
      icon: Ghost,
    },
    {
      title: "Design time wasted",
      description: "Every time you update your page, you have to open a design tool to generate a new social preview.",
      icon: Timer,
    },
    {
      title: "Poor brand alignment",
      description: "Mismatched colors and logos make even great pages feel untrustworthy, so fewer people tap through.",
      icon: Palette,
    },
  ];

  const industryUseCases = [
    {
      title: "Nike ecommerce makeover",
      description:
        "See the modeled $117M upside when Nike-level stores swap dull OG previews for on-brand, high-converting share cards.",
      cta: "How the Nike remake works",
      href: "/case-studies/nike-store",
    },
    {
      title: "Brooklyn Botanic Garden launch",
      description:
        "Learn how event imagery with dates, times, and CTAs could fuel a $225K annual lift for cultural experiences.",
      cta: "Tour the BBG scenario",
      href: "/case-studies/brooklyn-botanic-garden",
    },
    {
      title: "Office Ladies podcast boost",
      description:
        "Discover how episode-specific OG art can drive 2K+ extra listens and keep sponsorship CPMs climbing.",
      cta: "See the podcast results",
      href: "/case-studies/office-ladies-podcast",
    },
  ];

  const integrations = [
    {
      name: "Carrd",
      status: "Live guide",
      description:
        "Replace Carrd’s default screenshot with a branded OG Pilot image in minutes — use Publish → Media → Share Image.",
      url: "/carrd-social-image",
    },
    {
      name: "WordPress",
      status: "Live guide",
      description:
        "Install the OG Pilot plugin, connect via OAuth, and auto-generate branded social tags for posts, pages, and products.",
      url: "/wordpress",
    },
    {
      name: "Claude / ChatGPT MCP",
      status: "Live guide",
      description:
        "Add https://ogpilot.com/mcp as a custom connector. Debug, compress, and palette work as a guest. Sign in with an active plan to generate images.",
      url: "/docs/mcp",
    },
    {
      name: "Next.js",
      status: "Live guide",
      description:
        "Generate signed OG URLs server-side in App Router metadata so each route gets a unique preview image.",
      url: "/nextjs-og-image-generator",
    },
    {
      name: "Ruby on Rails",
      status: "Live guide",
      description:
        "Use og_pilot_ruby in controllers/views to set per-page og:image values without hand-rolling JWT code.",
      url: "/rails-og-image-generator",
    },
    {
      name: "Astro",
      status: "Live guide",
      description: "Generate OG image URLs in server context and inject them into layout metadata for social cards.",
      url: "/astro-og-image-generator",
    },
    {
      name: "Shopify",
      status: "Coming soon",
      description:
        "Native app is on the way—sync product data automatically and refresh visuals with every inventory update.",
    },
    {
      name: "Webflow",
      status: "Coming soon",
      description: "Connect your CMS collections and launch automation-ready visuals straight from Webflow Designer.",
    },
  ];

  // SDKs from docs - keep in sync with app/frontend/pages/docs/index.tsx
  const sdks = [
    {
      language: "Ruby",
      package: "og_pilot_ruby",
      version: "~> 0.4.1",
      href: "https://github.com/sunergos-ro/og_pilot_ruby",
      docsHref: "/docs#getting-started",
      iconClass: "bg-[#CC342D]/15 text-[#CC342D] dark:bg-[#CC342D]/25 dark:text-[#e5534b]",
    },
    {
      language: "JavaScript",
      package: "og-pilot-js",
      version: "^0.4.2",
      href: "https://github.com/sunergos-ro/og-pilot-js",
      docsHref: "/docs#getting-started",
      iconClass: "bg-[#F7DF1E]/25 text-[#b8860b] dark:bg-[#F7DF1E]/20 dark:text-[#f7df1e]",
    },
    {
      language: "Python",
      package: "og-pilot",
      version: "==0.4.1",
      href: "https://github.com/sunergos-ro/og-pilot-python",
      docsHref: "/docs#getting-started",
      iconClass: "bg-[#3776AB]/15 text-[#3776AB] dark:bg-[#3776AB]/25 dark:text-[#4584b6]",
    },
    {
      language: "PHP",
      package: "og-pilot-php",
      version: "",
      href: "https://github.com/sunergos-ro/og-pilot-php",
      docsHref: "/docs#getting-started",
      iconClass: "bg-[#777BB4]/15 text-[#777BB4] dark:bg-[#777BB4]/25 dark:text-[#8892bf]",
    },
  ];

  return (
    <>
      <Head title="The dynamic Open Graph image generator" />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 pb-24 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <PublicHeader />

        {/* Hero Section */}
        <section className="container mx-auto max-w-2xl px-4 pt-6 pb-16 sm:pt-10 sm:pb-24 md:max-w-6xl">
          <div className="mx-auto grid items-start gap-8 md:grid-cols-[1.85fr_0.3fr_1fr] md:items-center">
            <div className="w-full space-y-6 md:pt-4">
              <div className="inline-flex items-center justify-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-1 text-xs font-medium text-orange-600 shadow-sm dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-200">
                Domain-First Open Graph Automation
              </div>
              <div className="space-y-4">
                <h1 className="text-5xl font-bold text-balance text-gray-900 md:text-6xl lg:text-7xl dark:text-white">
                  The dynamic Open Graph image generator
                </h1>
                <p className="max-w-2xl text-lg text-balance text-gray-600 md:text-xl dark:text-gray-300">
                  Most links look random on social media. OG Pilot auto-generates on-brand social previews so more
                  people click on them.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="h-12 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-6 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:from-orange-600 hover:to-orange-700 hover:shadow-xl focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none"
                >
                  <Link
                    href="/new"
                    onClick={() => {
                      void trackEvent("click-home-generate");
                    }}
                  >
                    Generate an OG image
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 rounded-xl border-orange-200 bg-white/80 px-6 text-base font-semibold text-gray-900 hover:bg-orange-50 dark:border-orange-500/40 dark:bg-gray-900/60 dark:text-white dark:hover:bg-orange-500/10"
                >
                  <Link
                    href="/docs/mcp"
                    onClick={() => {
                      void trackEvent("click-home-mcp");
                    }}
                  >
                    Use with MCP
                  </Link>
                </Button>
              </div>

              <ul className="max-w-2xl space-y-4 text-base text-gray-600 lg:text-lg dark:text-gray-300">
                {heroHighlights.map((highlight) => (
                  <li key={highlight.copy} className="flex gap-3">
                    <span aria-hidden="true">{highlight.emoji}</span>
                    <span>{highlight.copy}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex justify-center md:-mt-12">
              <ArrowRight className="hidden h-16 w-24 text-gray-400 md:block dark:text-orange-900" />
              <ArrowBottom className="visible h-24 w-12 text-gray-400 md:hidden dark:text-orange-900" />
            </div>

            <div className="relative mx-auto w-full max-w-md md:max-w-none">
              <div
                className="absolute -top-6 right-0 bottom-6 left-0 z-0 rounded-3xl bg-gradient-to-br from-orange-400 via-orange-300 to-orange-500 opacity-80 blur-2xl"
                aria-hidden="true"
              />
              <Card className="relative z-10 overflow-hidden border border-orange-200/60 bg-white/95 shadow-2xl dark:border-orange-500/30 dark:bg-gray-900/85">
                <div
                  className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-orange-300/45 to-amber-200/30 blur-3xl dark:from-orange-500/20 dark:to-amber-500/15"
                  aria-hidden="true"
                />
                <CardContent className="relative space-y-4 sm:space-y-5">
                  <form onSubmit={handleHeroDomainSubmit} className="space-y-4">
                    <div className="flex items-center rounded-xl bg-white pr-2 pl-3 text-gray-900 shadow-sm ring-1 ring-gray-200 transition-shadow focus-within:ring-2 focus-within:ring-orange-500 dark:bg-gray-800 dark:text-white dark:ring-gray-700 dark:focus-within:ring-orange-400">
                      <div className="flex h-10 w-8 shrink-0 items-center justify-center text-gray-400 dark:text-gray-500">
                        {faviconUrl ? (
                          <img src={faviconUrl} alt="Website favicon" className="h-6 w-6 rounded" />
                        ) : (
                          <span className="text-xl">🌐</span>
                        )}
                      </div>
                      <Input
                        ref={urlInputRef}
                        id="domain-input"
                        name="domain"
                        type="text"
                        inputMode="url"
                        placeholder="yourdomain.com"
                        className="h-12 flex-1 border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
                        value={heroDomainForm.data.domain}
                        onChange={handleHeroDomainChange}
                        onPaste={handleDomainPaste}
                        disabled={heroDomainForm.processing}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={!isValidDomain || heroDomainForm.processing}
                      className="h-12 w-full rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-8 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:from-orange-600 hover:to-orange-700 hover:shadow-xl focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {heroDomainForm.processing ? "Checking domain…" : "Connect domain"}
                    </Button>
                  </form>

                  {heroDomainForm.errors.domain ? (
                    <p className="text-sm text-red-600 dark:text-red-400">{heroDomainForm.errors.domain}</p>
                  ) : null}
                  <p className="mx-auto mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-medium text-orange-600 dark:text-orange-400">7-day free trial</span>
                    <span className="mx-2">•</span>
                    <span>No credit card required</span>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <div className="container mx-auto max-w-6xl px-4">
          <ActivationGuide
            id="start-here"
            title="Generate, check, or automate the preview"
            links={[
              {
                href: "/new",
                label: "Generate an OG image",
                detail:
                  "One URL in, a branded card out. Use it for a launch page, a Carrd site, or a post that is not on a connected domain.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "See the Facebook, X, LinkedIn, Slack, Discord, and WhatsApp card before you send the link.",
              },
              {
                href: "/docs",
                label: "API docs",
                detail: "Sign an image URL from your server so every route can publish its own og:image.",
              },
              {
                href: "/docs/mcp",
                label: "MCP connector",
                detail: "Let an agent generate the same image from a URL, without a design handoff.",
              },
            ]}
          >
            <p>
              The domain form on this page is for a site you want refreshed on a schedule. If you only need one image,
              start at generate. If a link is already live and the unfurl looks wrong, start at the debugger, then come
              back and generate a replacement. WordPress sites can skip the upload with the{" "}
              <Link href="/wordpress" className="font-medium text-orange-700 hover:underline dark:text-orange-300">
                WordPress open graph plugin
              </Link>
              . Carrd sites follow the{" "}
              <Link
                href="/carrd-social-image"
                className="font-medium text-orange-700 hover:underline dark:text-orange-300"
              >
                Carrd social image
              </Link>{" "}
              upload after the image exists.
            </p>
          </ActivationGuide>
        </div>

        {/* Industry Problem Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-100/70 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950">
          <div className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
            <div className="mb-12 text-center">
              <p className="mb-3 text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase">
                THE COSTLY DESIGN TIMESINK
              </p>
              <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
                Social previews cost you time and money
              </h2>
              <p className="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-300">
                Traditional creation methods require you to open a design tool, use AI that often creates poor results,
                or hire a designer. And the result:
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {industryProblems.map((problem) => {
                const Icon = problem.icon;
                return (
                  <div
                    key={problem.title}
                    className="group relative overflow-hidden rounded-2xl border border-orange-200/40 bg-white/95 p-8 shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-orange-500/30 dark:bg-gray-900/80"
                  >
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-100/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-orange-500/20"
                      aria-hidden="true"
                    />
                    <div className="relative flex h-full flex-col gap-5">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100 text-xl text-orange-600 dark:bg-orange-500/20 dark:text-orange-200">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{problem.title}</h3>
                      <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                        {problem.description}
                      </p>
                      <div className="mt-auto h-1 w-16 rounded-full bg-orange-200/80 transition-colors duration-300 group-hover:bg-orange-400/60 dark:bg-orange-500/30 dark:group-hover:bg-orange-400/60" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <FeatureSection />

        {/* Before/After Proof Section */}
        <section className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
              From broken to branded in one share preview
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Here&apos;s the exact transformation OG Pilot creates automatically once your domain is connected.
            </p>
          </div>
          <div className="@container mt-24">
            <HeroBeforeAfterGallery
              before={heroPreviewGallery.before}
              after={heroPreviewGallery.after}
              className="w-full"
            />
          </div>
        </section>

        {/* Gallery Showcase Section */}
        <section id="gallery-showcase" className="mx-auto w-full px-4 py-16 sm:px-8 sm:py-24">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
              Proof: real links, auto-generated previews
            </h2>
            <p className="mx-auto mb-12 max-w-2xl text-xl text-gray-600 dark:text-gray-300">
              From personal blogs to product pages and events, OG Pilot keeps share cards branded and ready without
              manual design work.
            </p>
          </div>

          <WhenVisible
            data="gallery"
            buffer={200}
            fallback={
              <div className="flex h-64 items-center justify-center">
                <div className="text-gray-500">Loading gallery...</div>
              </div>
            }
          >
            <GalleryCarousel />
          </WhenVisible>

          <div className="mt-12 text-center">
            <a
              href="/gallery"
              className="inline-flex items-center gap-2 text-lg font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
            >
              View all examples
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>

        {/* Agency Case Studies */}
        <section
          id="case-studies"
          className="bg-gradient-to-br from-gray-50 via-white to-orange-50/40 py-16 sm:py-24 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950"
        >
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mb-12 text-center">
              <p className="mb-3 text-xs font-semibold tracking-[0.35em] text-orange-600 uppercase dark:text-orange-300">
                CASE STUDIES
              </p>
              <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
                How companies can turn OG Pilot into retainer wins
              </h2>
              <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                Explore the playbooks companies can use to ship better social previews in minutes, prove results with
                analytics, and expand retainers across ecommerce, events, and podcasts.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {industryUseCases.map((useCase) => (
                <Link key={useCase.title} href={useCase.href} className="group block h-full">
                  <Card className="relative flex h-full flex-col overflow-hidden border border-orange-200/50 bg-white/95 shadow-lg transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:shadow-xl dark:border-orange-500/30 dark:bg-gray-900/85">
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-100/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-orange-500/20"
                      aria-hidden="true"
                    />
                    <CardContent className="relative flex h-full flex-col space-y-5 p-8">
                      <div>
                        <CardTitle className="text-2xl text-gray-900 dark:text-white">{useCase.title}</CardTitle>
                        <CardDescription className="mt-3 flex-1 text-base leading-relaxed text-gray-600 dark:text-gray-300">
                          {useCase.description}
                        </CardDescription>
                      </div>
                      <span className="relative mt-auto inline-flex items-center gap-2 text-sm font-semibold text-orange-600 transition-colors group-hover:text-orange-700 dark:text-orange-300 dark:group-hover:text-orange-200">
                        {useCase.cta} →
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SDKs Section */}
        <section className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
          <div className="mb-12 text-center">
            <p className="mb-3 text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase dark:text-orange-300">
              DEVELOPER FIRST
            </p>
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
              Native SDKs for every stack
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-300">
              Don&apos;t hack together URL strings. Use our libraries to sign tokens, handle caching, and keep your API
              keys secure on the server.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {sdks.map((sdk) => (
              <a
                key={sdk.language}
                href={sdk.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-xl focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none dark:focus:ring-orange-500"
              >
                <Card className="relative flex h-full flex-col overflow-hidden border border-orange-200/50 bg-white/95 shadow-lg transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:shadow-xl dark:border-orange-500/30 dark:bg-gray-900/85">
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-100/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-orange-500/20"
                    aria-hidden="true"
                  />
                  <CardContent className="relative flex flex-col items-center gap-3 p-5">
                    <div
                      className={`inline-flex items-center justify-center rounded-xl px-3 py-1.5 text-sm font-bold ${sdk.iconClass}`}
                      aria-hidden
                    >
                      {sdk.language}
                    </div>
                    <div className="space-y-0.5 text-center">
                      <code className="block text-sm text-gray-600 dark:text-gray-400">{sdk.package}</code>
                      {sdk.version ? (
                        <span className="text-xs text-gray-500 dark:text-gray-500">{sdk.version}</span>
                      ) : null}
                    </div>
                    <span className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 transition-colors group-hover:text-orange-700 dark:text-orange-300 dark:group-hover:text-orange-200">
                      View on GitHub
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </section>

        {/* Integrations */}
        <section className="container mx-auto max-w-6xl px-4 py-16 sm:py-24">
          <div className="mb-12 text-center">
            <p className="mb-3 text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase">NO-CODE READY</p>
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
              Works with the tools you use
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-300">
              From site builders to CMS platforms, we&apos;re building the connectors you need to ship branded previews
              everywhere.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {integrations.map((integration) => {
              const content = (
                <Card
                  key={integration.name}
                  className="h-full border border-orange-200 bg-white/95 shadow-[0_4px_32px_0_rgba(255,153,51,0.09)] transition-shadow hover:shadow-lg dark:border-orange-500/30 dark:bg-gray-900/80"
                >
                  <CardHeader className="flex flex-col gap-2 pt-6 pb-3">
                    <div className="flex items-center justify-between gap-3">
                      <CardTitle className="text-[1.09rem] font-bold text-gray-900 dark:text-white">
                        {integration.name}
                      </CardTitle>
                      <Badge
                        variant="outline"
                        className={`rounded px-2 py-0.5 text-xs font-semibold tracking-tight transition-colors ${
                          integration.status === "Live guide"
                            ? "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-400 dark:bg-orange-500/10 dark:text-orange-200"
                            : "border-orange-200 bg-amber-50 text-amber-900 dark:border-orange-400 dark:bg-orange-500/20 dark:text-orange-100"
                        }`}
                      >
                        {integration.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 pb-7">
                    <CardDescription className="text-base text-gray-600 dark:text-gray-300">
                      {integration.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );

              return integration.url ? (
                <a
                  key={integration.name}
                  href={integration.url}
                  className="block rounded-xl focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none dark:focus:ring-orange-500"
                >
                  {content}
                </a>
              ) : (
                content
              );
            })}
          </div>
        </section>

        {/* Pricing Section */}
        <section
          id="pricing"
          className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-100/50 py-16 sm:py-24 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950"
        >
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mb-16 text-center">
              <p className="mb-3 text-xs font-semibold tracking-[0.35em] text-orange-600 uppercase dark:text-orange-300">
                UPGRADE WHEN YOU&apos;RE READY
              </p>
              <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl dark:text-white">
                Connect as many domains as you need
              </h2>
              <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                Expand as your business grows and you add more pages to your websites.
              </p>
            </div>

            <PricingTable />

            <div className="mx-auto mt-12 max-w-3xl">
              <Card className="border border-orange-200 bg-white/95 shadow-xl dark:border-orange-500/30 dark:bg-gray-900/85">
                <CardContent className="p-6 sm:p-8">
                  <form onSubmit={handlePricingDomainSubmit} className="space-y-5">
                    <p className="text-center text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase sm:text-left dark:text-orange-300">
                      Ready to get started?
                    </p>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                      <div className="flex flex-1 items-center rounded-md bg-white/90 pr-2 pl-3 text-gray-900 outline-1 -outline-offset-1 outline-gray-200 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-orange-500 dark:bg-black/60 dark:text-white dark:outline-white/10 dark:focus-within:outline-orange-400">
                        <div className="h-7 w-7 shrink-0 rounded text-base text-gray-500 select-none sm:text-sm/6 dark:text-gray-400">
                          {faviconUrl ? (
                            <img src={faviconUrl} alt="Website favicon" className="h-7 w-7 rounded" />
                          ) : (
                            <span className="h-7 w-7 rounded text-xl dark:text-gray-500">🌐</span>
                          )}
                        </div>
                        <Input
                          ref={pricingDomainInputRef}
                          name="pricing-domain"
                          type="text"
                          inputMode="url"
                          placeholder="yourdomain.com"
                          className="flex-1 border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
                          value={heroDomainForm.data.domain}
                          onChange={handleHeroDomainChange}
                          onPaste={handleDomainPaste}
                          disabled={heroDomainForm.processing}
                        />
                      </div>
                      <Button
                        type="submit"
                        className="h-11 w-full bg-orange-500 text-white transition-colors hover:bg-orange-600 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-6"
                        disabled={!isValidDomain || heroDomainForm.processing}
                      >
                        {heroDomainForm.processing ? "Checking…" : "Connect domain"}
                      </Button>
                    </div>
                    <p className="text-center text-sm text-gray-500 sm:text-left dark:text-gray-400">
                      7-day free trial. No credit card required.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 space-y-3 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Every plan starts with a 7-day free trial, daily refreshes, and cross-channel analytics.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Need something custom?{" "}
                <a
                  href="mailto:sales@ogpilot.com"
                  className="text-orange-600 hover:text-orange-700 dark:text-orange-400"
                >
                  Talk with our team
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Generation Form */}
        <section id="one-off-form" className="bg-gray-900 py-16 text-white dark:bg-black">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="mb-10 text-center">
              <p className="mb-3 text-xs font-semibold tracking-[0.3em] text-orange-600 uppercase">
                NEED A QUICK ONE-OFF?
              </p>
              <h2 className="text-3xl font-bold md:text-4xl">Need just one preview right now?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-white/80">
                Not ready to connect a domain yet? Drop a URL and get 2 branded OG image options in seconds.
              </p>
            </div>
            <Card className="mx-auto max-w-2xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur">
              <CardContent className="p-8">
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center rounded-md bg-white/90 pr-2 pl-3 text-gray-900 outline-1 -outline-offset-1 outline-gray-200 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-orange-500 dark:bg-black/60 dark:text-white dark:outline-white/10 dark:focus-within:outline-orange-400">
                      <Input
                        name="url"
                        type="text"
                        placeholder="https://yourpage.com"
                        className="w-full rounded-none border-0 bg-transparent text-base shadow-none focus-visible:ring-0 dark:bg-black/60"
                        value={data.url}
                        onChange={handleInputUrlChange}
                        onPaste={handleInputUrlPaste}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={processing}
                      className="flex h-11 min-w-[200px] items-center justify-center rounded-md bg-orange-500 px-5 text-base font-semibold text-white shadow transition-colors duration-200 hover:bg-orange-600 focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {processing ? (
                        "Processing..."
                      ) : (
                        <span className="flex items-center gap-2">{getCtaLabel("Generate my image")}</span>
                      )}
                      <span className="ml-2 text-lg">→</span>
                    </Button>
                    {renderPlanQuotaNotice("dark")}
                  </div>

                  {errors.url && <p className="mt-2 text-sm text-red-300">{errors.url}</p>}

                  {!flags.free_one_off_generation && (
                    <p className="mt-3 text-center text-xs leading-tight text-white/70">
                      <span className="font-medium text-orange-300">Unlimited refinements</span> • No subscription •
                      One-time payment
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        <FaqSection />

        {/* Support + Final CTA */}
        <FinalCta
          title="Connect your domain and keep every share on-brand"
          description="OG Pilot auto-pulls your brand colors and logo, refreshes every 24 hours, and gives your team SDKs for fast integrations."
          highlights={[
            {
              icon: "🔄",
              text: "24-hour refreshes for connected pages",
            },
            {
              icon: "🧩",
              text: "SDKs for quick integration across your stack",
            },
          ]}
          submitLabel="Connect domain"
          subtext="7-day free trial. No credit card required."
        />

        <SiteFooter />
      </div>

      <div className="fixed right-0 bottom-0 z-50 rounded-tl-md border border-orange-300 bg-white/80 p-2.5 text-xs font-semibold text-gray-800 shadow-md backdrop-blur dark:border-orange-800 dark:bg-gray-900/80 dark:text-gray-200">
        by{" "}
        <a
          href="https://x.com/RaulOnRails"
          target="_blank"
          className="text-orange-600 underline-offset-2 hover:text-orange-700 hover:underline dark:text-orange-400 dark:hover:text-orange-300"
          rel="noreferrer"
        >
          @RaulOnRails
        </a>
      </div>

      <StickySubscribeForm />
    </>
  );
}
